import { ChevronRight } from 'lucide-react';
import { Badge } from '@servicecompressionllc/sc-design-system';
import { Toggle } from '../../components/Toggle.js';
import type { AssistantPolicy, User } from '../../lib/types.js';
import { useSetPolicyActiveLogic } from './useGuardrailsLogic.js';
import { updatedMeta } from './shared.js';

/**
 * One policy document as a clickable row (same pattern as the Groups table):
 * the row opens the document's page, where download/delete live. The only
 * control on the row is the enforcement toggle.
 */
export const PolicyCard = ({
  tenantId,
  policy,
  users,
  onOpen,
}: {
  tenantId: string;
  policy: AssistantPolicy;
  users: User[];
  onOpen: (policy: AssistantPolicy) => void;
}) => {
  const activeLogic = useSetPolicyActiveLogic(tenantId);

  const toggleLabel = `${policy.isActive ? 'Disable' : 'Enable'} ${policy.name}`;

  const handleToggle = () => {
    void activeLogic.setActive(policy.id, !policy.isActive).catch(() => {
      // Surfaced via activeLogic.error — a refused enable (budget) must not
      // leave the switch showing a state the server rejected.
    });
  };

  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`Open ${policy.name}`}
      onClick={() => onOpen(policy)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(policy);
        }
      }}
      className="border-outline bg-surface-elevated hover:bg-surface focus-visible:outline-primary cursor-pointer rounded-xl border p-4 shadow-sm focus-visible:outline focus-visible:outline-2"
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-ink truncate text-[15px] font-semibold">{policy.name}</p>
            <Badge variant={policy.isActive ? 'success' : 'neutral'}>
              {policy.isActive ? 'ACTIVE' : 'DISABLED'}
            </Badge>
          </div>
          <p className="text-ink-subtle mt-0.5 text-xs">{updatedMeta(policy, users)}</p>
        </div>
        {/* The toggle is its own control — clicks on it must not open the row. */}
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <Toggle
            checked={policy.isActive}
            onChange={handleToggle}
            label={toggleLabel}
            disabled={activeLogic.pendingId === policy.id}
          />
        </div>
        <ChevronRight size={16} aria-hidden className="text-ink-subtle flex-none" />
      </div>

      {activeLogic.error && (
        <p className="text-danger mt-2.5 text-xs font-semibold">{activeLogic.error.message}</p>
      )}
    </div>
  );
};
